import * as systemConnectionsActions from '@Actions/systemConnections';
import { TestStatusType } from '@Components/TestCard';
import { getStatus } from '@Services/device';
import { testSystemConnectionAsync } from '@Services/systemConnections';
import {
	TestSystemConnectionRequestODBC,
	TestSystemConnectionRequestOPCUA,
} from '@Types/TestSystemConnectionRequest';
import {
	SystemConnectionShadow,
	SystemConnectionTestResult,
} from '@Types/systemConnectionTest';
import {
	error as snackbarError,
	warning as snackbarWarn,
	success,
} from '@innovyze/stylovyze';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { Task } from 'redux-saga';
import {
	call,
	cancel,
	cancelled,
	delay,
	fork,
	put,
	take,
	takeLatest,
} from 'redux-saga/effects';

const SHADOW_NAME = 'SysConTest';
const INITIAL_DELAY_SEC = 3; // ? possible lambda's cold start time
const BETWEEN_POLLING_TRIES_DELAY_SEC = 5; // ? delay between polling tries

type OperationResult = TestStatusType;
interface ResponseSystemConnectionTest {
	uid: string;
	operationResult: OperationResult;
}

function* systemConnectionTestSaga(
	action: PayloadAction<{
		credentials: Record<string, string>;
		timeoutSec: number;
	}>,
) {
	const {
		systemConnectionTestAsyncPoll, //
		systemConnectionTestAsyncRejected,
	} = systemConnectionsActions;

	const { credentials, timeoutSec } = action.payload;

	try {
		let testRequest:
			| TestSystemConnectionRequestODBC
			| TestSystemConnectionRequestOPCUA;

		if (credentials.connectionString) {
			testRequest = new TestSystemConnectionRequestODBC(
				credentials.username,
				credentials.password,
				credentials.connectionString,
				credentials.type,
				credentials.systemConnectionId,
			);
		} else if (credentials.opcuaUrl) {
			testRequest = new TestSystemConnectionRequestOPCUA(
				credentials.username,
				credentials.password,
				credentials.opcuaUrl,
				credentials.sessionName,
				credentials.endpointMustExist === 'true',
				credentials.securityMode,
				credentials.securityPolicy,
				credentials.systemConnectionId,
			);
		} else {
			throw new Error('Incomplete credentials');
		}

		const response: AxiosResponse<ResponseSystemConnectionTest> =
			yield call(testSystemConnectionAsync, testRequest);
		const result = response?.data;

		if (result?.operationResult === 'timeout') {
			yield put(snackbarError('System Connection Test Timed Out'));
			yield put(
				systemConnectionTestAsyncRejected(result?.operationResult),
			);
		} else if (result?.operationResult === 'success') {
			yield put(
				systemConnectionTestAsyncPoll({
					responseSysConTest: result,
					timeoutSec,
				}),
			);
		} else {
			yield put(snackbarError('Error Testing System Connection!'));
			yield put(
				systemConnectionTestAsyncRejected(result?.operationResult),
			);
		}
	} catch (e) {
		console.error(e);
		yield put(snackbarError('Error Testing System Connection!'));
		yield put(systemConnectionTestAsyncRejected('error'));
		console.error('systemConnectionTest Failed');
	}
}

type PollResponse = AxiosResponse<SystemConnectionShadow>;
function* pollSystemConnectionTestSaga(
	action: PayloadAction<systemConnectionsActions.AsyncPollSysConnTestActionPayload>,
) {
	const {
		systemConnectionTestAsyncResolved,
		systemConnectionTestAsyncRejected,
		systemConnectionTestAsyncPollStop,
		systemConnectionTestAsyncPollResolved,
		systemConnectionTestAsyncPollRejected,
		systemConnectionTestAsyncPollTimeout,
	} = systemConnectionsActions;
	const {
		responseSysConTest: { uid },
	} = action.payload;

	function getReported(
		sh: SystemConnectionShadow,
	): SystemConnectionTestResult {
		return sh?.status?.payload?.state?.reported;
	}

	try {
		if (!uid) throw new Error('Invalid uid id');
		const shadowName = `${SHADOW_NAME}-${uid}`;
		while (true) {
			const res: PollResponse = yield call(getStatus, shadowName);
			const result = getReported(res.data);
			if (result?.payload?.status === 'FINISHED') {
				const opResult = result?.payload?.operationResult;
				if (opResult === 'success') {
					yield put(success('Successfully Tested!'));
					yield put(systemConnectionTestAsyncResolved(opResult));
					yield put(systemConnectionTestAsyncPollResolved());
					yield put(systemConnectionTestAsyncPollStop());
				} else if (opResult === 'timeout') {
					yield put(snackbarError('Test Timed Out'));
					yield put(systemConnectionTestAsyncRejected(opResult));
					yield put(systemConnectionTestAsyncPollRejected());
					yield put(systemConnectionTestAsyncPollTimeout());
				} else if (opResult === 'failed' || opResult === 'error') {
					yield put(snackbarError('Operation failed'));
					yield put(systemConnectionTestAsyncRejected(opResult));
					yield put(systemConnectionTestAsyncPollRejected());
					yield put(systemConnectionTestAsyncPollStop());
				} else {
					const resStr = JSON.stringify(result?.payload);
					const errMsg = `Unexpected response received: ${resStr}`;
					throw new Error(errMsg);
				}
			} else if (result?.payload?.status === 'ERROR') {
				yield put(systemConnectionTestAsyncPollStop()); // * Stop polling
				yield put(systemConnectionTestAsyncPollRejected()); // * Stop polling (error)
				return;
			} else {
				yield delay(1000 * BETWEEN_POLLING_TRIES_DELAY_SEC); // * Poll continues...
			}
		}
	} catch (e) {
		console.error(e);
		yield put(snackbarError('Poll sensor state failed'));
		yield put(systemConnectionTestAsyncPollRejected());
	} finally {
		// * This block gets called if the task (saga) gets cancelled
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (yield cancelled()) console.log('poll was cancelled');
	}
}

function* stopSystemConnectionTestStateSaga(
	action: PayloadAction<systemConnectionsActions.AsyncPollSysConnTestActionPayload>,
) {
	const { systemConnectionTestAsyncPollTimeout } = systemConnectionsActions;
	const tOut = action.payload.timeoutSec;
	console.log(`poll connection Async will stop after ${tOut} seconds!`);
	try {
		yield delay(tOut * 1000);
		yield put(snackbarWarn('Connection test stopped'));
		yield put(systemConnectionTestAsyncPollTimeout());
	} catch (error) {
		console.log(error);
	} finally {
		// * This block gets called if the task (saga) gets cancelled
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (yield cancelled()) console.log('stop condition was cancelled');
	}
}

function* watchSystemConnectionTest() {
	yield takeLatest(
		systemConnectionsActions.systemConnectionTestAsync,
		systemConnectionTestSaga,
	);
}

function* watchPollSystemConnectionTestState() {
	const {
		systemConnectionTestAsyncPoll,
		systemConnectionTestAsyncPollStop,
		systemConnectionTestAsyncPollTimeout,
		systemConnectionTestAsyncPollResolved,
		systemConnectionTestAsyncPollRejected,
	} = systemConnectionsActions;
	// * Custom implementation, similar to takeLatest but it's cancellable. Check pollSystemConnectionResults
	while (true) {
		const action: PayloadAction<systemConnectionsActions.AsyncPollSysConnTestActionPayload> =
			yield take([systemConnectionTestAsyncPoll]);
		console.log('systemConnectionTestAsyncPoll action taken!');
		yield delay(1000 * INITIAL_DELAY_SEC);
		const task: Task = yield fork(
			pollSystemConnectionTestSaga,
			action as never,
		);
		const stopConditionTask: Task = yield fork(
			stopSystemConnectionTestStateSaga,
			action,
		);
		yield take([
			systemConnectionTestAsyncPollTimeout,
			systemConnectionTestAsyncPollStop,
			systemConnectionTestAsyncPollResolved,
			systemConnectionTestAsyncPollRejected,
		]);
		if (task.isRunning()) yield cancel(task);
		if (stopConditionTask.isRunning()) yield cancel(stopConditionTask);
	}
}

export default [
	watchSystemConnectionTest(), //
	watchPollSystemConnectionTestState(),
];
